import { useState } from "react";
import { Link } from "react-router-dom";
import { User, Briefcase, Mail, Phone, LayoutGrid, MapPin, DollarSign, Calendar, PencilLine, Upload, ShieldCheck, ArrowRight, Loader2, MessageCircle, PhoneCall, CheckCircle2, Users, Wrench, Award, Leaf } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const HERO_IMG = "https://images.unsplash.com/photo-1540541338287-41700207dee6?auto=format&fit=crop&w=2400&q=85";

const WHY_CONSULT = [
  "Expert advice from industry professionals",
  "Tailored solutions for your unique project",
  "Innovative, sustainable & cost-effective approach",
  "End-to-end support from concept to completion",
  "Commitment to safety, quality & excellence",
];

const REASONS = [
  { icon: Users, title: "Expert Team", copy: "Our experts bring decades of experience in marine and waterfront projects." },
  { icon: Wrench, title: "Custom Solutions", copy: "We design solutions that fit your needs, goals and budget." },
  { icon: Award, title: "Proven Results", copy: "Successful delivery of projects across Africa and beyond." },
  { icon: Leaf, title: "Sustainable Impact", copy: "We build infrastructure that protects communities and the environment." },
];

export default function RequestConsultation() {
  const [form, setForm] = useState({
    full_name: "",
    company: "",
    email: "",
    phone: "",
    service_interest: "",
    project_location: "",
    budget: "",
    timeline: "",
    message: "",
    consent: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const on = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) {
      toast.error("Please agree to the terms and privacy policy.");
      return;
    }
    setSubmitting(true);
    try {
      // Backend LeadCreate uses message field for details — include timeline in message
      const payload = { ...form };
      payload.message = `${payload.message}\n\nTimeline: ${payload.timeline || "—"}`;
      delete payload.timeline;
      delete payload.consent;
      const { data } = await api.post("/leads", payload);
      toast.success(data.message);
      setSent(true);
      setForm({ full_name: "", company: "", email: "", phone: "", service_interest: "", project_location: "", budget: "", timeline: "", message: "", consent: false });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      {/* HERO */}
      <section className="relative bg-[color:var(--mia-navy)] text-white overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Waterfront" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-r from-[color:var(--mia-navy)] via-[color:var(--mia-navy)]/85 to-[color:var(--mia-navy)]/25" />
        </div>
        <div className="mia-container py-14 lg:py-12 lg:py-20 relative">
          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight">
            REQUEST A<br />
            <span className="text-[color:var(--mia-green)]">CONSULTATION</span>
          </h1>
          <div className="w-16 h-1 bg-[color:var(--mia-green)] my-7" />
          <p className="text-white/85 max-w-lg leading-relaxed">
            Tell us about your project and our experts will provide tailored solutions that meet your needs.
            We're here to help you build sustainable, innovative and safe marine infrastructure.
          </p>
        </div>
      </section>

      {/* Form + Sidebar */}
      <section className="py-16 bg-white">
        <div className="mia-container grid lg:grid-cols-[1.5fr,1fr] gap-10">
          {/* CONSULTATION FORM */}
          <div>
            <div className="font-display font-bold text-[color:var(--mia-navy)] text-lg tracking-[0.14em] uppercase">Consultation Request Form</div>
            <div className="w-10 h-[3px] bg-[color:var(--mia-green)] mt-3 mb-8" />

            {sent ? (
              <div className="p-8 bg-emerald-50 border border-emerald-200 rounded-md flex items-start gap-4" data-testid="rc-success">
                <CheckCircle2 className="w-8 h-8 text-emerald-500 shrink-0" />
                <div>
                  <div className="font-display font-bold text-[color:var(--mia-navy)]">Request received.</div>
                  <p className="text-[color:var(--mia-muted)] mt-2 text-sm">Our team will get back to you within 24 hours.</p>
                  <button onClick={() => setSent(false)} className="mia-btn mia-btn-outline-navy mt-4" data-testid="rc-reset">Submit another request</button>
                </div>
              </div>
            ) : (
              <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="rc-form">
                {[
                  { icon: User, k: "full_name", ph: "Full Name *", req: true },
                  { icon: Briefcase, k: "company", ph: "Company / Organization *", req: true },
                  { icon: Mail, k: "email", ph: "Email Address *", type: "email", req: true },
                  { icon: Phone, k: "phone", ph: "Phone Number *", req: true },
                ].map((f) => (
                  <div key={f.k} className="relative">
                    <f.icon className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[color:var(--mia-muted)]" />
                    <input required={f.req} type={f.type || "text"} placeholder={f.ph} value={form[f.k]} onChange={on(f.k)} className="w-full pl-11 pr-4 py-3.5 bg-white border border-slate-200 rounded-md focus:outline-none focus:border-[color:var(--mia-navy)]" data-testid={`rc-${f.k}`} />
                  </div>
                ))}
                <div className="relative sm:col-span-2">
                  <LayoutGrid className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[color:var(--mia-muted)]" />
                  <select required value={form.service_interest} onChange={on("service_interest")} className="w-full pl-11 pr-4 py-3.5 bg-white border border-slate-200 rounded-md focus:outline-none focus:border-[color:var(--mia-navy)] text-[color:var(--mia-navy)]" data-testid="rc-service">
                    <option value="">Project Type / Service of Interest *</option>
                    <option>Coastal Protection</option>
                    <option>Floating Infrastructure</option>
                    <option>Waterfront Development</option>
                    <option>Marine Access Systems</option>
                    <option>Artificial Lakes & Water Features</option>
                    <option>Other</option>
                  </select>
                </div>
                <div className="relative">
                  <MapPin className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[color:var(--mia-muted)]" />
                  <input required placeholder="Project Location *" value={form.project_location} onChange={on("project_location")} className="w-full pl-11 pr-4 py-3.5 bg-white border border-slate-200 rounded-md focus:outline-none focus:border-[color:var(--mia-navy)]" data-testid="rc-location" />
                </div>
                <div className="relative">
                  <DollarSign className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[color:var(--mia-muted)]" />
                  <select value={form.budget} onChange={on("budget")} className="w-full pl-11 pr-4 py-3.5 bg-white border border-slate-200 rounded-md focus:outline-none focus:border-[color:var(--mia-navy)] text-[color:var(--mia-navy)]" data-testid="rc-budget">
                    <option value="">Estimated Budget</option>
                    <option>Under $100k</option>
                    <option>$100k – $500k</option>
                    <option>$500k – $2M</option>
                    <option>$2M – $10M</option>
                    <option>$10M+</option>
                  </select>
                </div>
                <div className="relative sm:col-span-2">
                  <Calendar className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[color:var(--mia-muted)]" />
                  <select value={form.timeline} onChange={on("timeline")} className="w-full pl-11 pr-4 py-3.5 bg-white border border-slate-200 rounded-md focus:outline-none focus:border-[color:var(--mia-navy)] text-[color:var(--mia-navy)]" data-testid="rc-timeline">
                    <option value="">Project Timeline</option>
                    <option>Immediate (0–3 months)</option>
                    <option>Short-term (3–6 months)</option>
                    <option>Medium-term (6–12 months)</option>
                    <option>Long-term (12+ months)</option>
                    <option>Just exploring</option>
                  </select>
                </div>
                <div className="sm:col-span-2 relative">
                  <PencilLine className="w-4 h-4 absolute left-4 top-4 text-[color:var(--mia-muted)]" />
                  <textarea required rows={5} value={form.message} onChange={on("message")} placeholder="Tell us about your project / requirements *
(Provide as much detail as possible)" className="w-full pl-11 pr-4 py-3.5 bg-white border border-slate-200 rounded-md focus:outline-none focus:border-[color:var(--mia-navy)] resize-none" data-testid="rc-message" />
                  <div className="text-right text-xs text-[color:var(--mia-muted)] mt-1">{form.message.length} / 1000</div>
                </div>
                <div className="sm:col-span-2 border-2 border-dashed border-slate-200 rounded-md p-6 text-center bg-[color:var(--mia-grey)]">
                  <Upload className="w-6 h-6 mx-auto text-[color:var(--mia-navy)]" />
                  <div className="mt-3 text-sm font-semibold text-[color:var(--mia-navy)]">Upload Project Files (Optional)</div>
                  <div className="text-xs text-[color:var(--mia-muted)] mt-1">Drag and drop files here or <span className="text-[color:var(--mia-green)] font-semibold">browse</span></div>
                  <div className="text-[0.68rem] text-[color:var(--mia-muted)] mt-1">PDF, DOC, DOCX, JPG, PNG (Max. 10MB)</div>
                  <div className="text-[0.65rem] text-[color:var(--mia-muted)]/70 mt-2">(File upload coming soon)</div>
                </div>
                <label className="sm:col-span-2 flex items-start gap-3 text-sm text-[color:var(--mia-text)]">
                  <input type="checkbox" checked={form.consent} onChange={on("consent")} className="mt-1 w-4 h-4 accent-[color:var(--mia-navy)]" data-testid="rc-consent" required />
                  <span>I agree to the <a href="#" className="text-[color:var(--mia-green)] hover:underline">terms</a> and <a href="#" className="text-[color:var(--mia-green)] hover:underline">privacy policy</a>. By submitting this form, I consent to Marine Infrastructure Africa contacting me regarding my inquiry.</span>
                </label>
                <button type="submit" disabled={submitting} className="mia-btn mia-btn-navy sm:col-span-2 py-4" data-testid="rc-submit">
                  {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting…</> : <>Submit Request <ArrowRight className="w-4 h-4" /></>}
                </button>
                <div className="sm:col-span-2 flex items-start gap-2 text-xs text-[color:var(--mia-muted)] mt-2">
                  <ShieldCheck className="w-4 h-4 text-[color:var(--mia-green)] shrink-0 mt-0.5" />
                  <span>Your information is safe with us. We respect your privacy and will never share your details with third parties.</span>
                </div>
              </form>
            )}
          </div>

          {/* SIDEBAR */}
          <div className="space-y-6">
            <div>
              <div className="font-display font-bold text-[color:var(--mia-navy)] text-lg tracking-[0.14em] uppercase">Speak To Our Team</div>
              <p className="text-sm text-[color:var(--mia-muted)] mt-2">Prefer to talk directly? Our team is ready to assist you.</p>
            </div>

            {[
              { icon: MessageCircle, iconBg: "bg-[color:var(--mia-green)]", title: "Chat On WhatsApp", note: "Chat with our team instantly", value: "+234 000 000 0000", href: "https://wa.me/2340000000000" },
              { icon: PhoneCall, iconBg: "bg-[color:var(--mia-navy)]", title: "Call Us", note: "Mon – Fri: 8:00 AM – 6:00 PM (WAT)", value: "+234 000 000 0000", href: "tel:+2340000000000" },
              { icon: Mail, iconBg: "bg-[color:var(--mia-navy)]", title: "Email Us", note: "We respond within 24 hours.", value: "info@marineinfrastructure.africa", href: "mailto:info@marineinfrastructure.africa" },
            ].map((c) => (
              <a key={c.title} href={c.href} target={c.href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" className="block bg-[color:var(--mia-grey)] rounded-md p-6 card-lift" data-testid={`speak-${c.title.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="flex gap-4 items-start">
                  <div className={`${c.iconBg} text-white w-12 h-12 rounded-full flex items-center justify-center shrink-0`}>
                    <c.icon className="w-6 h-6" />
                  </div>
                  <div>
                    <div className="font-display font-bold text-[color:var(--mia-navy)] text-sm tracking-[0.14em] uppercase">{c.title}</div>
                    <div className="text-xs text-[color:var(--mia-muted)] mt-1">{c.note}</div>
                    <div className="text-sm text-[color:var(--mia-green)] font-bold mt-1">{c.value}</div>
                  </div>
                </div>
              </a>
            ))}

            <div className="bg-white border border-slate-100 rounded-md p-6">
              <div className="font-display font-bold text-[color:var(--mia-navy)] text-base tracking-[0.14em] uppercase">Why Consult With Us?</div>
              <ul className="mt-5 space-y-3">
                {WHY_CONSULT.map((w) => (
                  <li key={w} className="flex items-start gap-3 text-sm text-[color:var(--mia-text)]">
                    <CheckCircle2 className="w-5 h-5 text-[color:var(--mia-green)] shrink-0 mt-0.5" />
                    <span>{w}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* 4 Reasons */}
      <section className="py-16 bg-[color:var(--mia-grey)]">
        <div className="mia-container grid grid-cols-2 lg:grid-cols-4 gap-6">
          {REASONS.map((r) => (
            <div key={r.title} className="text-center" data-testid={`reason-${r.title.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className="mx-auto w-14 h-14 flex items-center justify-center text-[color:var(--mia-navy)]"><r.icon className="w-8 h-8" strokeWidth={1.5} /></div>
              <div className="font-display font-bold text-[color:var(--mia-navy)] mt-4 text-sm tracking-[0.14em] uppercase">{r.title}</div>
              <p className="text-xs text-[color:var(--mia-muted)] mt-3 leading-relaxed">{r.copy}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-10 lg:py-12 bg-white">
        <div className="mia-container">
          <div className="bg-[color:var(--mia-navy)] rounded-md p-6 sm:p-8 lg:p-10 flex flex-col lg:flex-row gap-6 items-start lg:items-center text-white">
            <div className="w-14 h-14 rounded-full bg-[color:var(--mia-green)] text-white flex items-center justify-center shrink-0">
              <MessageCircle className="w-7 h-7" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display font-black text-xl sm:text-2xl leading-tight">Let's Build Something Great Together</div>
              <div className="text-sm text-white/70 mt-2">Partner with Marine Infrastructure Africa to bring your vision to life.</div>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
              <a href="https://wa.me/2340000000000" target="_blank" rel="noopener noreferrer" className="mia-btn mia-btn-primary w-full sm:w-auto" data-testid="rc-cta-whatsapp">
                <MessageCircle className="w-4 h-4" /> Chat On WhatsApp
              </a>
              <a href="tel:+2340000000000" className="mia-btn mia-btn-outline-white w-full sm:w-auto" data-testid="rc-cta-call">
                <PhoneCall className="w-4 h-4" /> Call Now
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
